import {useCallback, useEffect, useRef, useState} from 'react';
import {loadTodaySnapshot} from './todayActivitySnapshot';
import {selectTodayCoachActions, selectTodayNextAction} from './todayNextAction';
import {subscribeForeground, useIsForeground} from '../../services/AppStateService';

// A separate read keeps persisted feed filters and its pagination fences intact.
export function useTodayAction({backendService, activities, currentUserId, getDateKey, hasPlan, locked, enabled = true, reconcileWorkoutStarts, captureReconciliation}) {
  const foreground = useIsForeground();
  const [snapshot,setSnapshot]=useState({activities:[],loaded:false});
  const [revision,setRevision]=useState({number:0,passive:false});
  const [now,setNow]=useState(Date.now);
  const retry=useCallback(()=>setRevision(previous=>({number:previous.number+1,passive:false})),[]);
  const generation=useRef(0);
  const lastReadInputs=useRef(null);
  useEffect(()=>subscribeForeground(active=>{if(active){setNow(Date.now());setRevision(previous=>({number:previous.number+1,passive:true}));}}),[]);
  useEffect(()=>{
    if (!foreground || !enabled) return undefined;
    // Local clock only: no polling/API service. Handles timezone date boundaries
    // and expiry while foregrounded without reordering or focusing the feed.
    const clock = Date.now();
    const boundaries = snapshot.activities.flatMap(a => [a.created_at, a.due_at])
      .filter(Boolean).map(value => new Date(value).getTime() + 1).filter(value => value > clock);
    const delay = Math.min(60000 - (clock % 60000), ...boundaries.map(value => value - clock));
    const timer=setTimeout(()=>setNow(Date.now()),delay);
    return ()=>clearTimeout(timer);
  },[now,snapshot.activities,foreground,enabled]);
  const dayKey=getDateKey(now);
  const relevantRevision=JSON.stringify(activities.filter(a => {
    const created = a.created_at && new Date(a.created_at).getTime();
    const completed = a.completed_at && new Date(a.completed_at).getTime();
    return (Number.isFinite(created) && getDateKey(created) === dayKey)
      || (Number.isFinite(completed) && getDateKey(completed) === dayKey)
      || (['pending','ongoing','in_progress','started'].includes(a.status)
        && ((!a.due_at && a.type === 'workout' && a.status !== 'pending') || new Date(a.due_at).getTime() >= now));
  }).map(a => [a._virtualId || a._id,a.status,a.name,a.created_at,a.completed_at,a.due_at,a.updated_at,a.travel_replacement_id,a.travel_replacement_locked]).sort((a,b)=>String(a[0]).localeCompare(String(b[0]))));
  useEffect(()=>{
    if (!enabled || !currentUserId || locked) return undefined;
    const controller=new AbortController();
    const request=++generation.current;
    const readSequence=captureReconciliation?.();
    // A same-scope foreground read is background reconciliation, not an
    // interaction lock. Changed feed data, viewer, or day still locks as usual.
    const passive=revision.passive && lastReadInputs.current?.relevantRevision === relevantRevision
      && lastReadInputs.current?.currentUserId === currentUserId && lastReadInputs.current?.dayKey === dayKey;
    lastReadInputs.current={relevantRevision,currentUserId,dayKey};
    setSnapshot(previous=>({...previous,refreshing:true,passiveRefresh:passive}));
    loadTodaySnapshot(backendService,{signal:controller.signal})
      .then(data=>{if(request===generation.current&&!controller.signal.aborted)setSnapshot({...data,readSequence,dayKey,currentUserId,loaded:true,error:false,refreshing:false});})
      .catch(()=>{if(request===generation.current&&!controller.signal.aborted)setSnapshot(previous=>({
        activities:previous.dayKey===dayKey && previous.currentUserId===currentUserId ? previous.activities : [],
        readSequence:previous.dayKey===dayKey && previous.currentUserId===currentUserId ? previous.readSequence : readSequence,
        dayKey,currentUserId,loaded:true,error:true,refreshing:false,
      }));});
    return ()=>controller.abort();
  },[backendService,relevantRevision,currentUserId,revision,dayKey,locked,enabled,captureReconciliation]);
  const sameScope = snapshot.dayKey === dayKey && snapshot.currentUserId === currentUserId;
  const rows = sameScope && reconcileWorkoutStarts ? reconcileWorkoutStarts(snapshot.activities, snapshot.readSequence) : snapshot.activities;
  const retainedStart = sameScope && rows.some(row => ['ongoing','in_progress','started'].includes(row.status)
    && activities.some(local => local._id === row._id && local.status === row.status));
  const reconciled = {...snapshot,activities:rows,loaded:snapshot.loaded && sameScope,error:snapshot.error && sameScope && !retainedStart};
  const action=selectTodayNextAction({...reconciled,currentUserId,getDateKey,hasPlan,now});
  const actions=selectTodayCoachActions({...reconciled,currentUserId,getDateKey,hasPlan,now});
  if (retainedStart && snapshot.error) actions.push({kind:'error',title:'Today refresh failed; saved workout start kept',label:'Retry'});
  return {action,actions,snapshot:reconciled,retry,now,refreshing:Boolean(snapshot.refreshing && !snapshot.passiveRefresh && !retainedStart)};
}
